export type Truthy<T> = T extends false | "" | 0 | null | undefined ? never : T;

declare global {
  interface Array<T> {
    groupBy<K extends keyof any>(key: (x: T) => K): Record<K, T[]>;
    unique(mapper?: (value: T) => string): Array<T>;
    nonNullable(): Array<NonNullable<T>>;
    truthy(): Array<Truthy<T>>;
    toMap<K extends keyof any>(key: (x: T) => K): Map<K, T>;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.toMap) {
  Array.prototype.toMap = function toMap<T, K extends keyof any>(
    this: T[],
    key: (x: T) => K,
  ): Map<K, T> {
    const map = new Map<K, T>();

    for (const item of this) {
      map.set(key(item), item);
    }

    return map;
  };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.toSorted) {
  Array.prototype.toSorted = function <T>(
    this: T[],
    compareFn?: (a: T, b: T) => number,
  ): T[] {
    const result: T[] = Array.from({ length: this.length });

    for (let i = 0; i < this.length; i++) {
      result[i] = this[i]!;
    }

    return result.sort(compareFn);
  };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.groupBy) {
  Array.prototype.groupBy = function groupBy<T, K extends keyof any>(
    this: T[],
    key: (x: T) => K,
  ): Record<K, T[]> {
    return this.reduce(
      (groups, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (groups[key(item)] ||= []).push(item);
        return groups;
      },
      {} as Record<K, T[]>,
    );
  };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.unique) {
  Array.prototype.unique = function <T>(
    this: T[],
    mapper = (value: T) => {
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    },
  ) {
    const lookup = new Set<string>();

    return this.filter((value: T): boolean => {
      const key = mapper(value);

      if (lookup.has(key)) {
        return false;
      }
      lookup.add(key);

      return true;
    });
  };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.nonNullable) {
  const filter = <T>(value: T): value is NonNullable<T> => {
    return value !== null && value !== undefined;
  };
  Array.prototype.nonNullable = function <T>(this: T[]) {
    return this.filter(filter);
  };
}

export const truthy = <T>(value: T): value is Truthy<T> => {
  return !!value;
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Array.prototype.truthy) {
  Array.prototype.truthy = function <T>(this: T[]) {
    return this.filter(truthy);
  };
}

export {};
